import { ApolloClient, ApolloQueryResult } from "@apollo/client"
import { getEnv } from "@pathwright/utils"
import {
  ContextualFeaturePermissionDocument,
  ContextualFeaturePermissionQuery,
  ContextualFeaturePermissionQueryResult,
  ContextualFeaturePermissionQueryVariables,
  FeatureAction,
  FeatureKey,
  FeaturePermission,
  useContextualFeaturePermissionQuery
} from "@pathwright/web/src/modules/api/generated"
import { getApolloClient } from "@pathwright/web/src/modules/pathwright/PathwrightClient"
import useHomeActiveEntries from "../home/useHomeActiveEntries"

export const canViewUnifiedHomeVariables = {
  featureKey: FeatureKey.HomeScreenUnified,
  featureAction: FeatureAction.HomeScreenView
}

// Once we have the feature permission query result, we can check
// if the user can view the unified home screen.
// A null return indicates the permission hasn't loaded yet.
export function getCanUnifiedViewHome(
  featurePermQuery:
    | ContextualFeaturePermissionQueryResult
    | ApolloQueryResult<ContextualFeaturePermissionQuery>
): null | boolean {
  if (featurePermQuery.data) {
    // Check that Everyone can view the unified Home Screen.
    const canViewUnifiedHome =
      featurePermQuery.data.contextualFeaturePermission
        ?.permission_in_context === FeaturePermission.Everyone

    return canViewUnifiedHome
  }

  return null
}

// Via client.query.
export async function canViewUnifiedHome() {
  const client = getApolloClient() as ApolloClient<any>
  const featurePermQuery = await client.query<
    ContextualFeaturePermissionQuery,
    ContextualFeaturePermissionQueryVariables
  >({
    query: ContextualFeaturePermissionDocument,
    variables: canViewUnifiedHomeVariables
  })

  return getCanUnifiedViewHome(featurePermQuery)
}

export function useCanViewUnifiedHomeFeature() {
  const featurePermQuery = useContextualFeaturePermissionQuery({
    variables: canViewUnifiedHomeVariables
  })

  return featurePermQuery
}

export function useHasUnifiedHomePath() {
  const { getPath, path } = useHomeActiveEntries()
  // Check if the current path is the unified home path.
  const hasUnifiedHomePath = path === getPath("paths.unified")
  return hasUnifiedHomePath
}

// Via hook.
export function useCanViewUnifiedHome() {
  const canViewUnifiedHome = getCanUnifiedViewHome(
    useCanViewUnifiedHomeFeature()
  )
  const hasUnifiedPath = useHasUnifiedHomePath()

  return canViewUnifiedHome !== null
    ? canViewUnifiedHome || hasUnifiedPath
    : null
}

// Simplified nav check.
export function useCanViewSimpleNav() {
  const canViewUnifiedHome = useCanViewUnifiedHome()
  const hasUnifiedPath = useHasUnifiedHomePath()

  // The unified home feature itself doesn't allow for the simple nav updates in prod yet,
  // but you can access simple nave via the unified home path in any env.
  return canViewUnifiedHome !== null
    ? (canViewUnifiedHome && !getEnv("production")) || hasUnifiedPath
    : null
}

export default useCanViewUnifiedHome
